<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

const props = defineProps({
    enabled: {
        type: Boolean,
        default: true,
    },
    show: {
        required: true,
        type: Boolean,
        default: false,
    },
    title: {
        type: String,
        default: trans('global.navigation_away_title'),
    },
    message: {
        type: String,
        default: trans('global.navigation_away_message'),
    },
});

const isNavigatingAway = ref(false);
const isLoading = ref(false);
const href = ref(null);

const shouldOpen = computed(() => props.enabled && props.show && isNavigatingAway.value);

onMounted(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);

    document.addEventListener('click', handleLinkClick, true);
});

onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', handleBeforeUnload);

    document.removeEventListener('click', handleLinkClick, true);
});

const handleProceed = () => {
    isLoading.value = true;

    window.removeEventListener('beforeunload', handleBeforeUnload);

    window.location.href = href.value;
};

const handleClose = () => {
    isNavigatingAway.value = false;
}

const handleBeforeUnload = e => {
    if (!props.enabled || !props.show) return;

    e.preventDefault();
    e.returnValue = '';
};

const handleLinkClick = e => {
    if (!props.enabled || !props.show) return;

    const anchor = e.target.closest('[href]');
    if (!anchor) return;

    // Ignore links that open in new tab or downloads
    if (anchor.target === '_blank' || anchor.hasAttribute('download')) {
        return;
    }

    // Same-page hash links can be ignored
    href.value = anchor.getAttribute('href');
    if (!href.value || href.value.startsWith('#')) return;

    e.preventDefault();

    isNavigatingAway.value = true;
};
</script>

<template>
    <nord-modal
        @close="handleClose"
        @cancel="handleClose"
        id="navigate-away-modal"
        size="s"
        :open="shouldOpen"
        :aria-labelledby="title"
    >
        <h2 slot="header" id="title">{{ title }}</h2>

        <p class="n-reset">{{ message }}</p>

        <nord-button-group slot="footer" variant="spaced">
            <nord-button
                @click="handleClose"
                :disabled="isLoading"
                expand id="cancelButton"
            >
                {{ trans('global.cancel') }}
            </nord-button>

            <nord-button
                @click="handleProceed"
                :loading="isLoading"
                :disabled="isLoading"
                expand
                id="confirmButton"
                variant="danger"
                autofocus
            >
                {{ trans('global.continue') }}
            </nord-button>
        </nord-button-group>
    </nord-modal>
</template>